// gwp-progress-bar.js — Gift With Purchase tiered cart reward bar.
//
// Up to 3 spend tiers; each tier's reward is admin-set (free shipping OR one $0-priced
// free gift). Rewards are cumulative. The customer does not choose.
//
// Enforcement is native: gift products are priced $0 (free because they cost nothing),
// and free shipping is an AUTOMATIC free-shipping discount in Shopify (min spend,
// Canada only) that applies at checkout however the customer gets there — so this
// script never touches discount codes (it used to apply one, which missed express /
// Shop Pay checkouts and replaced customers' own codes like WELCOME10). Gift
// thresholds are enforced here + by the webhook-gwp-order-check.php backstop.
//
// This script:
//   - reads config from the #gwp-config <script> (the gwp.settings shop metafield)
//   - draws a tiered progress bar in every [data-gwp-slot]
//   - reconciles the cart to the unlocked tiers: adds/removes $0 gift lines
//
// Thresholds are measured against the cart's non-gift total BEFORE any discounts
// (original_line_price), matching how the offer is defined.
//
// Config (gwp.settings): { enabled, country, currency, free_shipping_code (unused),
//   tiers:[{ threshold, reward:"gift"|"shipping", gift:{variant_id,title,image,...} }],
//   copy:{ progress, unlocked_all, shipping_label } }   // tiers sorted ascending

(function () {
  "use strict";

  var cfgEl = document.getElementById("gwp-config");
  if (!cfgEl) return;

  var config = null;
  try {
    config = JSON.parse(cfgEl.textContent || "null");
  } catch (e) {
    config = null;
  }
  var buyerCountry = (cfgEl.getAttribute("data-country") || "").toUpperCase();

  if (!config || config.enabled !== true) return;
  var TIERS = (config.tiers || [])
    .filter(function (t) {
      return t && Number(t.threshold) > 0;
    })
    .sort(function (a, b) {
      return a.threshold - b.threshold;
    });
  if (!TIERS.length) return;
  if (!config.country || buyerCountry !== String(config.country).toUpperCase()) return;

  var CURRENCY = config.currency || "CAD";
  var COPY = config.copy || {};
  var GIFT_VIDS = {};
  TIERS.forEach(function (t) {
    if (t.reward === "gift" && t.gift && t.gift.variant_id) GIFT_VIDS[Number(t.gift.variant_id)] = true;
  });

  var lastCart = null;
  var busy = false;
  var pending = false;
  var renderTimer = null;

  // If /cart/add.js rejects a gift variant (draft product, out of stock, wrong market,
  // etc.) it still resolves with a 200-parseable JSON error body, not a fetch rejection —
  // so without this, reconcile() would see the gift still missing on every settle() and
  // retry immediately, forever. Back off per-variant instead of retrying in a tight loop.
  var GIFT_RETRY_COOLDOWN_MS = 15000;
  var failedGiftAdds = {}; // variant id -> Date.now() of last failed add

  /* --------------------------------------------------------------- reads */

  function isGiftItem(it) {
    if (!it) return false;
    if (it.properties && it.properties._gwp_gift) return true;
    return !!GIFT_VIDS[it.variant_id];
  }

  function nonGiftSubtotalCents(cart) {
    var sum = 0;
    (cart && cart.items ? cart.items : []).forEach(function (it) {
      if (isGiftItem(it)) return;
      // Before any discounts — the offer's threshold ignores other discounts.
      sum += it.original_line_price != null ? it.original_line_price : it.line_price || 0;
    });
    return sum;
  }

  function money(cents) {
    var v = (cents || 0) / 100;
    var whole = (cents || 0) % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-CA", {
        style: "currency",
        currency: CURRENCY,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(v);
    } catch (e) {
      return "$" + v.toFixed(whole ? 0 : 2);
    }
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function rewardName(tier) {
    if (tier.reward === "shipping") return COPY.shipping_label || "Free shipping";
    return (tier.gift && tier.gift.title) || "a free gift";
  }

  /* --------------------------------------------------------------- writes */

  function jpost(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json();
    });
  }

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } }).then(function (r) {
      return r.json();
    });
  }

  // The drawer refreshes itself on the `cart:refresh` DOM event (wired up by Concept's
  // own cart.js). The standalone /cart page has no equivalent listener — our
  // /cart/add.js + /cart/update.js calls never go through the theme's own pubsub, so
  // nothing tells <main-cart> to re-render. Refresh it directly, the same way Concept's
  // drawer refreshes #MiniCart-<id>: fetch this section's markup and swap its innerHTML.
  function refreshMainCartSection() {
    var el = document.querySelector("main-cart[data-section-id]");
    if (!el || !el.id) return Promise.resolve();
    var sectionId = el.getAttribute("data-section-id");
    return fetch(location.pathname + "?section_id=" + encodeURIComponent(sectionId))
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, "text/html").getElementById(el.id);
        if (fresh) el.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  function settle() {
    return getCart()
      .then(function (c) {
        lastCart = c;
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: c } }));
        document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } }));
        return refreshMainCartSection();
      })
      .then(function () {
        renderAll();
      })
      .finally(function () {
        busy = false;
        if (pending) {
          pending = false;
          reconcile();
        }
      });
  }

  function reconcile() {
    if (busy || !lastCart) return;
    var cart = lastCart;
    var sub = nonGiftSubtotalCents(cart);

    var desired = {};
    TIERS.forEach(function (t) {
      if (sub + 0.5 < t.threshold * 100) return;
      if (t.reward === "gift" && t.gift && t.gift.variant_id) desired[Number(t.gift.variant_id)] = true;
    });

    var giftLines = (cart.items || []).filter(isGiftItem);
    var have = {};
    giftLines.forEach(function (it) {
      have[it.variant_id] = it;
    });

    var now = Date.now();
    var toAdd = Object.keys(desired)
      .map(Number)
      .filter(function (v) {
        if (have[v]) return false;
        var failedAt = failedGiftAdds[v];
        return !(failedAt && now - failedAt < GIFT_RETRY_COOLDOWN_MS);
      });

    var updates = {};
    giftLines.forEach(function (it) {
      if (!desired[it.variant_id]) updates[it.key] = 0;
      else if (it.quantity !== 1) updates[it.key] = 1;
    });

    // Free-shipping tiers need no cart change: Shopify's automatic discount applies
    // free shipping at checkout. Only gift lines are reconciled here.
    if (!toAdd.length && !Object.keys(updates).length) {
      renderAll();
      return;
    }

    busy = true;
    var body = {};
    if (Object.keys(updates).length) body.updates = updates;

    (Object.keys(body).length ? jpost("/cart/update.js", body) : Promise.resolve())
      .then(function () {
        if (!toAdd.length) return null;
        return jpost("/cart/add.js", {
          items: toAdd.map(function (id) {
            return { id: id, quantity: 1, properties: { _gwp_gift: "1" } };
          }),
        }).then(function (res) {
          // /cart/add.js resolves with a normal JSON body even on rejection (draft
          // product, out of stock, wrong market, ...) — it's not a fetch error, so it
          // has to be checked explicitly or the loop above would retry it forever.
          if (res && (res.status || res.errors)) {
            toAdd.forEach(function (id) {
              failedGiftAdds[id] = Date.now();
            });
            console.error(
              "[GWP] could not add gift variant(s) " + toAdd.join(", ") + " — retrying in " + GIFT_RETRY_COOLDOWN_MS / 1000 + "s:",
              res.description || res.message || res.errors || res
            );
          } else {
            toAdd.forEach(function (id) {
              delete failedGiftAdds[id];
            });
          }
        });
      })
      .catch(function (err) {
        toAdd.forEach(function (id) {
          failedGiftAdds[id] = Date.now();
        });
        console.error("[GWP] cart update/add request failed:", err);
      })
      .then(settle);
  }

  /* --------------------------------------------------------------- render */

  function buildBarHTML(cart) {
    var sub = nonGiftSubtotalCents(cart);
    var maxT = TIERS[TIERS.length - 1].threshold * 100;
    var pct = Math.max(0, Math.min(sub / maxT, 1)) * 100;
    var isFull = sub + 0.5 >= maxT;

    var next = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (sub + 0.5 < TIERS[i].threshold * 100) {
        next = TIERS[i];
        break;
      }
    }

    var msg = next
      ? esc(
          (COPY.progress || "Add {{remaining}} more for {{reward}}")
            .replace("{{remaining}}", money(next.threshold * 100 - sub))
            .replace("{{reward}}", rewardName(next))
        )
      : esc(COPY.unlocked_all || "You've unlocked every reward") + " \u{1F389}";

    // Three aligned rows sharing the same left:X% mapping: amount callouts above the
    // track, the track + dots, and the reward labels below. The last tier always maps
    // to exactly 100% (it defines maxT), so it gets right-edge clamping (is-end) instead
    // of being centred on the edge and overflowing the card; a tier that lands very near
    // 0% gets the equivalent left-edge clamp (is-start).
    var amounts = '<div class="gwp-bar__amounts">';
    var meter =
      '<div class="gwp-bar__meter">' +
      // <span>, not <div> — Concept's theme.css hides `div:empty`, and the fill has no content
      '<div class="gwp-bar__track' +
      (isFull ? " is-full" : "") +
      '"><span class="gwp-bar__fill" style="width:' +
      pct.toFixed(1) +
      '%"></span></div>';
    var labels = '<div class="gwp-bar__labels">';

    TIERS.forEach(function (t, idx) {
      var at = Math.min((t.threshold * 100) / maxT, 1) * 100;
      var hit = sub + 0.5 >= t.threshold * 100;
      var hitClass = hit ? " is-hit" : "";
      var edgeClass = idx === TIERS.length - 1 ? " is-end" : at < 6 ? " is-start" : "";
      var atStyle = ' style="left:' + at.toFixed(1) + '%"';

      amounts += '<span class="gwp-bar__amount' + hitClass + edgeClass + '"' + atStyle + ">" + money(t.threshold * 100) + "</span>";
      meter += '<span class="gwp-bar__marker' + hitClass + edgeClass + '"' + atStyle + "></span>";
      labels +=
        '<span class="gwp-bar__label' +
        hitClass +
        edgeClass +
        '"' +
        atStyle +
        ">" +
        (hit ? "✓ " : "") +
        esc(rewardName(t)) +
        "</span>";
    });

    amounts += "</div>";
    meter += "</div>";
    labels += "</div>";

    return '<div class="gwp-bar" role="status"><p class="gwp-bar__msg">' + msg + "</p>" + amounts + meter + labels + "</div>";
  }

  function renderAll() {
    var html = lastCart && lastCart.item_count ? buildBarHTML(lastCart) : "";
    var slots = document.querySelectorAll("[data-gwp-slot]");
    for (var i = 0; i < slots.length; i++) {
      if (slots[i]._gwpHtml === html) continue;
      slots[i]._gwpHtml = html;
      slots[i].innerHTML = html;
    }
  }

  function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(renderAll, 50);
  }

  /* --------------------------------------------------------------- wire-up */

  document.addEventListener("cart:updated", function (e) {
    if (busy) {
      pending = true;
      return;
    }
    if (e.detail && e.detail.cart) lastCart = e.detail.cart;
    reconcile();
  });

  var drawer = document.getElementById("CartDrawer");
  if (drawer && "MutationObserver" in window) {
    new MutationObserver(scheduleRender).observe(drawer, { childList: true, subtree: true });
  }

  getCart().then(function (c) {
    lastCart = c;
    reconcile();
  });
})();
